import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { callingKeys } from '@/hooks/useCalling'
import { subscribeRealtime } from '@/hooks/useRealtime'
import { useCallStore } from '@/stores/call.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { callSchema } from '@/types/api/calling'

export const CALL_UPDATED_EVENT = 'call.updated'

/**
 * Applies `call.updated` events to the query cache, keeps the recording URL the user already had
 * (the stream never carries it) and puts new live calls of the user (e.g. inbound) in the dock.
 */
export function useCallRealtimeSync() {
  const queryClient = useQueryClient()
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)

  useEffect(() => {
    return subscribeRealtime(CALL_UPDATED_EVENT, (data) => {
      const parsed = callSchema.safeParse(data)
      if (!parsed.success) return
      const call = parsed.data
      const key = callingKeys.call(workspaceId, call.id)
      const previous = queryClient.getQueryData<typeof call>(key)
      queryClient.setQueryData(key, { ...call, recordingUrl: call.recordingUrl ?? previous?.recordingUrl ?? null })
      if (call.status !== 'ENDED') {
        useCallStore.getState().addCall(call.id)
      }
      if (call.recordId) {
        void queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId, 'call', 'record', call.recordId] })
        if (call.status === 'ENDED') {
          void queryClient.invalidateQueries({ queryKey: ['activities', call.recordId] })
        }
      }
    })
  }, [queryClient, workspaceId])
}
